/**
 * OracleShareComposer — full composer modal for publishing an Oracle exchange
 * to the Weekly feed. Replaces the previous "type a caption, hit Share" flow
 * with a preview-first experience where the author controls the framing.
 *
 * What's editable:
 *   • title    — headline for the Weekly card (defaults to question)
 *   • question — the community-facing version of what you asked. Prefills
 *                with the exact prompt sent to the Oracle; author can polish
 *                it into a proper question. A "use the exact question I
 *                asked" reset link and a "show what you actually typed"
 *                disclosure keep the original honest.
 *   • caption  — a pull-quote framing line ("what this opened up for me").
 *
 * What's locked (by design):
 *   • The Oracle's answer. Displayed as a read-only preview. The archive's
 *     integrity depends on the Oracle's voice arriving as-generated.
 */
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, Globe, Lock, Edit3, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const TITLE_MAX = 120;
const QUESTION_MAX = 500;
const CAPTION_MAX = 500;

// Sensible default title: strip surrounding whitespace, trim to fit, drop
// any trailing partial word so we don't end mid-token.
function defaultTitle(rawQuestion) {
  const q = (rawQuestion || "").trim();
  if (q.length <= 80) return q;
  const cut = q.slice(0, 80);
  const lastSpace = cut.lastIndexOf(" ");
  return (lastSpace > 40 ? cut.slice(0, lastSpace) : cut) + "…";
}

export default function OracleShareComposer({
  open,
  onClose,
  question: rawQuestion = "",
  response = "",
  onPublished,
}) {
  const [title, setTitle] = useState("");
  const [question, setQuestion] = useState("");
  const [caption, setCaption] = useState("");
  const [publishing, setPublishing] = useState(false);
  const titleRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setTitle(defaultTitle(rawQuestion));
    setQuestion(rawQuestion || "");
    setCaption("");
    // Focus after the portal mounts.
    setTimeout(() => { titleRef.current?.focus(); titleRef.current?.select(); }, 60);
  }, [open, rawQuestion]);

  // Close on Esc.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape" && !publishing) onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, publishing, onClose]);

  if (!open) return null;

  const canPublish = title.trim().length > 0 && question.trim().length > 0 && !publishing;

  const publish = async () => {
    if (!canPublish) return;
    setPublishing(true);
    try {
      const { data } = await api.post("/oracle/save", {
        title: title.trim(),
        question: question.trim(),
        response,
        caption: caption.trim(),
        visibility: "public",
        include_card: false,
      });
      toast.success("Shared to Weekly — and saved to your shelf.");
      onPublished?.(data.saved);
      onClose?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not publish.");
    } finally {
      setPublishing(false);
    }
  };

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Compose Weekly post"
      data-testid="oracle-share-composer"
      className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/60 backdrop-blur-sm overflow-y-auto p-4 sm:p-8"
      onMouseDown={(e) => { if (e.target === e.currentTarget && !publishing) onClose?.(); }}
    >
      <div className="relative w-full max-w-2xl bg-card border border-border shadow-2xl my-auto">
        {/* CLOSE */}
        <button
          type="button"
          onClick={() => !publishing && onClose?.()}
          disabled={publishing}
          data-testid="composer-close"
          aria-label="Close"
          className="absolute top-4 right-4 text-muted-foreground hover:text-foreground disabled:opacity-40"
        >
          <X size={18} />
        </button>

        <div className="p-6 sm:p-8">
          <div className="mono text-[10px] uppercase tracking-[0.22em] text-[hsl(var(--accent))] mb-6 flex items-center gap-2">
            <Globe size={12} /> This will publish to Weekly
          </div>

          {/* TITLE */}
          <label className="block">
            <span className="label mb-2 flex items-center gap-2">
              <Edit3 size={11} /> Title
            </span>
            <input
              ref={titleRef}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              data-testid="composer-title"
              className="input-line w-full serif text-2xl leading-tight"
              maxLength={TITLE_MAX}
              placeholder="Give this correlation a title"
            />
            <div className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-2">
              {title.length}/{TITLE_MAX}
            </div>
          </label>

          {/* QUESTION */}
          <div className="mt-8 pt-8 hairline-t">
            <label className="block">
              <span className="label mb-2 flex items-center gap-2">
                <Edit3 size={11} /> The question you&apos;re inviting the community into
              </span>
              <p className="serif italic text-muted-foreground mb-3">
                You asked the Oracle in your own voice. Polish it into a
                question the community can answer for themselves.
              </p>
              <textarea
                value={question}
                onChange={(e) => setQuestion(e.target.value)}
                data-testid="composer-question"
                rows={2}
                maxLength={QUESTION_MAX}
                className="input-line w-full serif italic text-[17px] leading-relaxed resize-none"
              />
              <div className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-2 flex items-center justify-between gap-3 flex-wrap">
                <span>{question.length}/{QUESTION_MAX}</span>
                <button
                  type="button"
                  onClick={() => setQuestion(rawQuestion || "")}
                  data-testid="composer-reset-question"
                  className="link-underline text-[hsl(var(--primary))]"
                >
                  use the exact question I asked
                </button>
              </div>
            </label>
            <details className="mt-3" data-testid="composer-show-raw-details">
              <summary className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground cursor-pointer hover:text-foreground">
                Show what you actually typed
              </summary>
              <p className="serif italic text-muted-foreground/70 leading-relaxed text-sm mt-2 pl-4 border-l border-border">
                &ldquo;{rawQuestion}&rdquo;
              </p>
            </details>
          </div>

          {/* RESPONSE (read-only) */}
          <div className="mt-8 pt-8 hairline-t">
            <div className="label mb-3 flex items-center gap-2">
              <Lock size={11} /> The Oracle&apos;s answer
            </div>
            <div
              data-testid="composer-response-preview"
              className="serif text-foreground/85 leading-relaxed whitespace-pre-line text-[15px] max-h-64 overflow-y-auto pr-2"
            >
              {response}
            </div>
            <p className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-3">
              Locked — the Oracle&apos;s voice stays honest as-generated.
            </p>
          </div>

          {/* CAPTION */}
          <div className="mt-8 pt-8 hairline-t">
            <label className="block">
              <span className="label mb-2 flex items-center gap-2">
                <Edit3 size={11} /> Your caption
              </span>
              <p className="serif italic text-muted-foreground mb-3">
                What did this open up for you? What would you love to hear from
                others? Optional — shows up as a pull-quote on your post.
              </p>
              <textarea
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                data-testid="composer-caption"
                rows={3}
                maxLength={CAPTION_MAX}
                placeholder="What did this open up for you?"
                className="input-line w-full text-[15px] leading-relaxed resize-none"
              />
              <div className="mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground mt-2">
                {caption.length}/{CAPTION_MAX}
              </div>
            </label>
          </div>

          {/* ACTIONS */}
          <div className="mt-8 pt-6 hairline-t flex items-center justify-end gap-3">
            <button
              type="button"
              onClick={() => !publishing && onClose?.()}
              disabled={publishing}
              data-testid="composer-cancel"
              className="mono text-[10px] uppercase tracking-[0.2em] px-4 py-3 text-muted-foreground hover:text-foreground disabled:opacity-40"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={publish}
              disabled={!canPublish}
              data-testid="composer-publish"
              className="btn-gold inline-flex items-center gap-2 disabled:opacity-40"
            >
              {publishing ? <Loader2 size={12} className="animate-spin" /> : <Globe size={12} />}
              {publishing ? "Publishing…" : "Publish to Weekly"}
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
